import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { PageHeader, fmtEUR } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

function startOfWeek(d) {
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  const s = new Date(d); s.setDate(d.getDate() + diff); s.setHours(0, 0, 0, 0);
  return s;
}
const dayNames = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export default function Shifts() {
  const { can } = useAuth();
  // FASE 7.4 STEP F.1 — shift mutations gate on shifts.write.
  const canWrite = can("shifts.write");
  const [shifts, setShifts] = useState([]);
  const [emps, setEmps] = useState([]);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", date: "", start: "09:00", end: "17:00", role: "", work_area: "unspecified" });

  const load = async () => {
    const [s, e] = await Promise.all([api.get("/shifts"), api.get("/employees")]);
    setShifts(s.data); setEmps(e.data);
  };
  useEffect(() => { load(); }, []);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(weekStart.getDate() + i);
    return d;
  }), [weekStart]);

  const shiftsFor = (date) => shifts.filter((s) => s.date === date.toISOString().slice(0, 10));

  const submit = async (e) => {
    e.preventDefault();
    const emp = emps.find((x) => x.id === form.employee_id);
    if (!emp) return;
    try {
      await api.post("/shifts", { ...form, employee_name: `${emp.first_name} ${emp.last_name}`, role: form.role || emp.role, work_area: form.work_area || "unspecified" });
      toast.success("Turno creato"); setOpen(false); load();
    } catch { toast.error("Errore"); }
  };
  const del = async (id) => { await api.delete(`/shifts/${id}`); load(); };

  // Costo settimana
  const weekCost = useMemo(() => {
    let total = 0;
    for (const d of days) {
      for (const s of shiftsFor(d)) {
        const [sh, sm] = s.start.split(":").map(Number);
        const [eh, em] = s.end.split(":").map(Number);
        const hours = ((eh * 60 + em) - (sh * 60 + sm)) / 60;
        const emp = emps.find((e) => e.id === s.employee_id);
        total += hours * Number(emp?.hourly_cost || 0);
      }
    }
    return total;
  }, [days, shifts, emps]);

  return (
    <div data-testid="shifts-page">
      <PageHeader eyebrow="Personale" title="Turni settimana"
        subtitle={`Costo stimato settimana: ${fmtEUR(weekCost)}`}
        actions={
          <div className="flex gap-2 items-center">
            <Button variant="outline" size="icon" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }} data-testid="prev-week"><ChevronLeft size={14} /></Button>
            <div className="text-sm px-3">{weekStart.toLocaleDateString("it-IT")}</div>
            <Button variant="outline" size="icon" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }} data-testid="next-week"><ChevronRight size={14} /></Button>
            <Dialog open={open} onOpenChange={setOpen}>
              {canWrite && (
              <DialogTrigger asChild><Button data-testid="new-shift-btn" className="bg-neutral-900 text-[#FDFBF7]"><Plus size={14} strokeWidth={1.5} /> Turno</Button></DialogTrigger>
              )}
              <DialogContent>
                <DialogHeader><DialogTitle className="font-serif">Nuovo turno</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                  <div><Label>Dipendente</Label><Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}><SelectTrigger data-testid="shift-emp"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Data</Label><Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Inizio</Label><Input type="time" required value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} /></div>
                    <div><Label>Fine</Label><Input type="time" required value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} /></div>
                  </div>
                  <div>
                    <Label>Area di lavoro</Label>
                    <Select value={form.work_area} onValueChange={(v) => setForm({ ...form, work_area: v })}>
                      <SelectTrigger data-testid="shift-work-area"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bar">Bar</SelectItem>
                        <SelectItem value="sala">Sala</SelectItem>
                        <SelectItem value="cucina">Cucina</SelectItem>
                        <SelectItem value="supporto">Supporto</SelectItem>
                        <SelectItem value="altro">Altro</SelectItem>
                        <SelectItem value="unspecified">Non specificata</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <DialogFooter><Button data-testid="shift-submit" type="submit" className="bg-neutral-900 text-[#FDFBF7]">Salva</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        } />

      <div className="grid grid-cols-7 gap-3">
        {days.map((d, i) => (
          <div key={i} className="border border-[#E2D9C8] rounded-lg p-3 bg-white min-h-[400px]">
            <div className="label-eyebrow">{dayNames[i]}</div>
            <div className="font-serif text-2xl leading-none mt-1 mb-3">{d.getDate()}</div>
            <div className="space-y-1.5">
              {shiftsFor(d).map((s) => (
                <div key={s.id} className="text-xs bg-[#F3EDE1] rounded p-2 group" data-testid={`shift-${s.id}`}>
                  <div className="font-medium">{s.employee_name}</div>
                  <div className="text-neutral-600">{s.start} — {s.end}</div>
                  <div className="text-neutral-400 text-[10px] capitalize">{s.role}</div>
                  {canWrite && (
                    <button onClick={() => del(s.id)} className="opacity-0 group-hover:opacity-100 text-red-700 text-[10px] mt-1" data-testid={`del-shift-${s.id}`}>Elimina</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
